import { useState, useEffect } from 'react';
import { UserCheck, UserX, Search } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminAPI } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const { t } = useSettings();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const fetch = () => { adminAPI.getUsers().then(r => setUsers(r.data.users)).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => { fetch(); }, []);

  const toggle = async (id, name, isActive) => {
    if (!window.confirm(`${isActive ? 'Deactivate' : 'Activate'} "${name}"?`)) return;
    try { await adminAPI.toggleUser(id); toast.success('Updated!'); fetch(); } catch { toast.error('Error'); }
  };

  const filtered = users.filter(u =>
    (!search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())) &&
    (!roleFilter || u.role === roleFilter)
  );

  return (
    <AdminLayout title={t('admin','users')}>
      <div className="admin-users-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Users', value: users.length, color: 'var(--accent)' },
          { label: 'Active', value: users.filter(u=>u.isActive).length, color: 'var(--success)' },
          { label: 'Admins', value: users.filter(u=>u.role==='admin').length, color: 'var(--gold)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <Search size={16} style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
          <input className="form-input" placeholder={t('admin','search')} value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 42, paddingTop: 10, paddingBottom: 10 }} />
        </div>
        <select className="form-input" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ maxWidth: 160, paddingTop: 10, paddingBottom: 10 }}>
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {loading ? <div className="flex-center" style={{ height: 300 }}><div className="spinner" /></div> : (
        <div className="card admin-table-wrap" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead><tr><th>User</th><th>Role</th><th>{t('admin','status')}</th><th>Phone</th><th>Joined</th><th>{t('admin','actions')}</th></tr></thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: u.role === 'admin' ? 'linear-gradient(135deg, var(--accent), var(--gold))' : 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, color: u.role === 'admin' ? 'white' : 'var(--text-secondary)', flexShrink: 0 }}>
                        {u.name[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ padding: '4px 12px', background: u.role === 'admin' ? 'var(--accent-dim)' : 'rgba(34,197,94,0.1)', color: u.role === 'admin' ? 'var(--accent)' : 'var(--success)', borderRadius: 8, fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{u.role}</span>
                  </td>
                  <td>
                    <span style={{ padding: '4px 12px', background: u.isActive ? 'rgba(34,197,94,0.1)' : 'rgba(244,63,94,0.1)', color: u.isActive ? 'var(--success)' : 'var(--danger)', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{u.phone || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td>
                    {u.role !== 'admin' && (
                      <button className="btn btn-sm" style={{ background: u.isActive ? 'rgba(244,63,94,0.1)' : 'rgba(34,197,94,0.1)', color: u.isActive ? 'var(--danger)' : 'var(--success)', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={() => toggle(u._id, u.name, u.isActive)}>
                        {u.isActive ? <><UserX size={14} /> Deactivate</> : <><UserCheck size={14} /> Activate</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No users found</div>}
        </div>
      )}
    </AdminLayout>
  );
}
